import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { Link, useBlocker, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import type { Id } from "@convex/_generated/dataModel";
import { PROGRAMME_LABELS, type DateAnchorValue, type ProgrammeValue } from "@convex/schemaEnums";
import { api } from "@convex/_generated/api";
import {
  LuCheck,
  LuChevronDown,
  LuCircleAlert,
  LuClipboardList,
  LuFileText,
  LuNetwork,
  LuPlus,
  LuTag,
  LuX,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Field,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  Menu,
  NativeSelect,
  Portal,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { MultiSelectTags } from "../components/MultiSelectTags";
import { PageHeader } from "../components/PageHeader";
import { toastError } from "../components/ui/toaster";
import { TextLink } from "../components/TextLink";
import { useViewerAccess } from "@/lib/access";
import {
  ActivityAdminAccessDenied,
  ActivityAdminLoadingState,
  buildActivityDraft,
  buildActor,
  buildTaskDueRule,
  buildFormSchema,
  buildReviewers,
  countReviewers,
  anchorOptions,
  buildDateRule,
  emptyActivityDraft,
  emptyTask,
  nextLocalKey,
  slugify,
  type EditableTask,
  type TagOption,
  type ActivityDetail,
  type ActivityDraft,
  type ActivitySummary,
} from "./ActivityAdminShared";
import {
  moveItem,
  SortableTaskTab,
  TaskPanel,
  taskIssues,
  VERTICAL_LIST_MODIFIERS,
} from "./ActivityEditorParts";

function SectionCard({
  title,
  description,
  icon: SectionIcon,
  children,
}: {
  title: string;
  description: string;
  icon: IconType;
  children: React.ReactNode;
}) {
  return (
    <Card.Root as="section" borderWidth="1px" borderColor="border" shadow="sm">
      <Card.Body gap={3} p={{ base: 4, sm: 5 }}>
        <HStack align="flex-start" gap={3}>
          <Icon as={SectionIcon} boxSize={6} flexShrink={0} mt={0.5} color="blue.500" />
          <Stack gap={0.5}>
            <Heading size="md">{title}</Heading>
            <Text maxW="3xl" fontSize="xs" color="fg.muted">{description}</Text>
          </Stack>
        </HStack>
        {children}
      </Card.Body>
    </Card.Root>
  );
}


/**
 * One date rule: what it is measured from, and how far. The offset box is
 * hidden for a fixed date, where an offset would mean nothing.
 */
function DateRuleFields({
  label,
  hint,
  anchor,
  offsetDays,
  fixedDay,
  onChange,
}: {
  label: string;
  hint: string;
  anchor: DateAnchorValue;
  offsetDays: string;
  fixedDay: string;
  onChange: (next: { anchor: DateAnchorValue; offsetDays: string; fixedDay: string }) => void;
}) {
  return (
    <Field.Root>
      <Field.Label>{label}</Field.Label>
      <Stack gap={2} w="full">
        <NativeSelect.Root>
          <NativeSelect.Field
            value={anchor}
            onChange={(e) => onChange({ anchor: e.target.value as DateAnchorValue, offsetDays, fixedDay })}
          >
            {anchorOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {anchor === "fixedDay" ? (
          <Input
            type="date"
            value={fixedDay}
            onChange={(e) => onChange({ anchor, offsetDays, fixedDay: e.target.value })}
          />
        ) : (
          <Input
            type="number"
            placeholder="days after, e.g. 90"
            value={offsetDays}
            onChange={(e) => onChange({ anchor, offsetDays: e.target.value, fixedDay })}
          />
        )}
        <Text fontSize="xs" color="fg.muted">{hint}</Text>
      </Stack>
    </Field.Root>
  );
}

export function ActivityEditorPage() {
  const params = useParams({ strict: false }) as { activityId?: string };
  const searchParams = useSearch({ strict: false }) as { cloneFrom?: string };
  const navigate = useNavigate();
  const isNew = !params.activityId || params.activityId === "new";
  const cloneFromId = isNew ? (searchParams.cloneFrom ?? null) : null;

  const access = useViewerAccess();
  const isActivityAdmin = access?.isActivityAdmin ?? false;

  const catalogue = useQuery(
    api.activities.listAllActivities,
    isActivityAdmin ? {} : "skip",
  ) as ActivitySummary[] | undefined;
  const tags = useQuery(
    api.activities.listTags,
    isActivityAdmin ? {} : "skip",
  ) as TagOption[] | undefined;

  const [flashMessage, setFlashMessage] = useState<{
    tone: "success" | "error";
    text: string;
  } | null>(null);
  const [activityDraft, setActivityDraft] = useState<ActivityDraft>(
    emptyActivityDraft,
  );
  const [cloneApplied, setCloneApplied] = useState(false);
  const [activeTaskIndex, setActiveTaskIndex] = useState(0);

  // Track the "clean" snapshot to detect unsaved changes
  const [cleanSnapshot, setCleanSnapshot] = useState<string>(
    JSON.stringify(emptyActivityDraft()),
  );
  const isDirty = useMemo(
    () => JSON.stringify(activityDraft) !== cleanSnapshot,
    [activityDraft, cleanSnapshot],
  );

  // Ref to track if we're intentionally navigating after save
  const isSavingRef = useRef(false);

  // Load the existing activity for editing
  const activeActivityDetail = useQuery(
    api.activities.getActivityDetail,
    isActivityAdmin && !isNew && params.activityId
      ? {
        activityId: params.activityId as Id<"activities">,
      }
      : "skip",
  ) as ActivityDetail | null | undefined;

  // Load the clone source
  const cloneSourceDetail = useQuery(
    api.activities.getActivityDetail,
    isActivityAdmin && cloneFromId
      ? {
        activityId: cloneFromId as Id<"activities">,
      }
      : "skip",
  ) as ActivityDetail | null | undefined;

  // The pool a named reviewer is picked from. CeMMies only: signing off is
  // something you do as a member of the institute.
  const directory = useQuery(api.persons.listCeMMies, isActivityAdmin ? {} : "skip");
  const reviewerCandidates = directory?.persons ?? [];

  const createActivity = useMutation(api.activities.createActivity);
  const updateActivity = useMutation(api.activities.updateActivity);

  // Populate the draft from the existing activity (edit mode)
  useEffect(() => {
    if (!isNew && activeActivityDetail) {
      const draft = buildActivityDraft(activeActivityDetail);
      setActivityDraft(draft);
      setCleanSnapshot(JSON.stringify(draft));
    }
  }, [activeActivityDetail, isNew]);

  // Populate draft from clone source (new mode with cloneFrom)
  useEffect(() => {
    if (isNew && cloneSourceDetail && !cloneApplied) {
      const draft = buildActivityDraft(cloneSourceDetail);
      draft.activityId = null; // a clone is a new activity
      draft.key = `${draft.key}-copy`;
      draft.title = `${draft.title} (copy)`;
      setActivityDraft(draft);
      setCleanSnapshot(JSON.stringify(draft));
      setCloneApplied(true);
    }
  }, [cloneSourceDetail, isNew, cloneApplied]);

  // ── Unsaved-changes guard: TanStack Router blocker + browser beforeunload ──
  useBlocker({
    shouldBlockFn: () => {
      if (isSavingRef.current) return false;
      if (!isDirty) return false;
      return !window.confirm(
        "You have unsaved changes. Are you sure you want to leave?",
      );
    },
    enableBeforeUnload: () => !isSavingRef.current && isDirty,
  });

  function flash(tone: "success" | "error", text: string) {
    setFlashMessage({ tone, text });
    setTimeout(() => setFlashMessage(null), 4000);
  }

  // ── Task operations ────────────────────────────────────────────────────────
  const updateTaskAt = useCallback(
    (index: number, updater: (task: EditableTask) => EditableTask) =>
      setActivityDraft((d) => ({
        ...d,
        tasks: d.tasks.map((t, i) => (i === index ? updater(t) : t)),
      })),
    [],
  );

  const addTask = useCallback(() => {
    setActivityDraft((d) => ({ ...d, tasks: [...d.tasks, emptyTask()] }));
    setActiveTaskIndex(activityDraft.tasks.length);
  }, [activityDraft.tasks.length]);

  const removeTaskAt = useCallback((index: number) => {
    setActivityDraft((d) => ({ ...d, tasks: d.tasks.filter((_, i) => i !== index) }));
    setActiveTaskIndex((prev) => Math.max(0, prev >= index ? prev - 1 : prev));
  }, []);

  const duplicateTaskAt = useCallback((index: number) => {
    setActivityDraft((d) => {
      const source = d.tasks[index];
      if (!source) return d;
      const next = [...d.tasks];
      const copy: EditableTask = {
        ...source,
        localKey: nextLocalKey(),
        // A copy starts with no prerequisites: pointing at the original's would
        // be a guess, and pointing at itself would be a deadlock.
        prerequisiteKeys: [],
        title: source.title ? `${source.title} (copy)` : "",
        reviewerRoles: [...source.reviewerRoles],
        reviewerPersonIds: [...source.reviewerPersonIds],
        fields: source.fields.map((f) => ({ ...f })),
      };
      next.splice(index + 1, 0, copy);
      return { ...d, tasks: next };
    });
    setActiveTaskIndex(index + 1);
  }, []);

  const reorderTasks = useCallback((from: number, to: number) => {
    setActivityDraft((d) => {
      if (to < 0 || to >= d.tasks.length) return d;
      return { ...d, tasks: moveItem(d.tasks, from, to) };
    });
    setActiveTaskIndex((prev) => {
      if (to < 0 || to >= activityDraft.tasks.length) return prev;
      if (prev === from) return to;
      if (from < prev && to >= prev) return prev - 1;
      if (from > prev && to <= prev) return prev + 1;
      return prev;
    });
  }, [activityDraft.tasks.length]);

  const moveTaskAt = useCallback(
    (index: number, direction: -1 | 1) => reorderTasks(index, index + direction),
    [reorderTasks],
  );

  const taskSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleTaskDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = Number(String(active.id).replace("task-", ""));
    const to = Number(String(over.id).replace("task-", ""));
    reorderTasks(from, to);
  }, [reorderTasks]);

  const handleSaveActivity = useCallback(async () => {
    // "After what this comes after" needs something to come after. Refused
    // here rather than saved and left to produce no date at all, which is
    // what it would do and which reads as a bug rather than as a gap.
    const waitsOnPredecessor = [
      activityDraft.startAnchor,
      activityDraft.opensAnchor,
      activityDraft.dueAnchor,
    ].includes("afterActivity");
    if (waitsOnPredecessor && activityDraft.dependsOnActivityIds.length === 0) {
      toastError(
        "This activity has no predecessor to wait for",
        "A date set to “after what this comes after” needs at least one activity listed under “Comes after”.",
      );
      return;
    }

    const payload = {
      key: activityDraft.key,
      title: activityDraft.title,
      description: activityDraft.description,
      tagIds: activityDraft.tagIds as Id<"activityTags">[],
      required: activityDraft.required,
      programme: activityDraft.programme,
      recommendedStartRule: buildDateRule(
        activityDraft.startAnchor,
        activityDraft.startOffsetDays,
        activityDraft.startFixedDay,
      ),
      opensOnRule: buildDateRule(
        activityDraft.opensAnchor,
        activityDraft.opensOffsetDays,
        activityDraft.opensFixedDay,
      ),
      dueRule: buildDateRule(
        activityDraft.dueAnchor,
        activityDraft.dueOffsetDays,
        activityDraft.dueFixedDay,
      ),
      tasks: activityDraft.tasks.map((task) => ({
        // Local keys become orders here, at the one point where positions are
        // finally fixed.
        prerequisiteOrders: task.prerequisiteKeys
          .map((key) => activityDraft.tasks.findIndex((candidate) => candidate.localKey === key) + 1)
          .filter((order) => order > 0),
        title: task.title,
        description: task.description,
        required: task.required,
        actor: buildActor(task),
        dueRule: buildTaskDueRule(task),
        reviewers: buildReviewers(task),
        // "any" is the default when it appears at all: needing every one of
        // several signatures is the rarer intent (model §9).
        reviewMode: countReviewers(task) > 0 ? (task.reviewMode ?? "any") : null,
        formSchema: task.satisfiedByParticipation ? null : buildFormSchema(task.fields),
        reviewFormSchema: buildFormSchema(task.reviewFields),
        // Sent even when off, as `undefined`, so that turning it off on an
        // existing step actually removes it: a save rewrites the task rows,
        // and a field left out of the payload is a field the rewrite drops.
        participationCriteria: task.satisfiedByParticipation
          ? {
            roles: task.participationRoles as never,
            seriesId: (task.participationSeriesId ?? null) as never,
            eventId: null,
            externalKind: (task.participationExternalKind ?? null) as never,
          }
          : undefined,
      })),
      dependsOnActivityIds: activityDraft.dependsOnActivityIds as Id<"activities">[],
    };

    try {
      if (activityDraft.activityId) {
        await updateActivity({
          activityId: activityDraft.activityId as Id<"activities">,
          isActive: activityDraft.isActive,
          ...payload,
        });
      } else {
        await createActivity(payload);
      }
      isSavingRef.current = true;
      void navigate({ to: "/activities/admin/catalogue" });
    } catch (error) {
      flash(
        "error",
        error instanceof Error ? error.message : "Saving the activity failed.",
      );
    }
  }, [activityDraft, createActivity, updateActivity, navigate]);

  // Use the *saved* title (not the live draft) for the page chrome, so editing
  // the title field doesn't imply the change has already been persisted.
  const savedTitle = activeActivityDetail?.title;
  const breadcrumbs = [
    { label: "CeMMbase", to: "/" },
    { label: "Activities", to: "/activities" },
    { label: "Activity catalogue", to: "/activities/admin/catalogue" },
    { label: isNew ? "New activity" : (savedTitle ?? "Edit activity") },
  ];

  // Which tasks currently have validation problems (surfaced on the tabs).
  // The two pickers are the costly part of the form around the tasks: a
  // combobox and a tags input apiece, both state machines. Built once, so
  // editing a TASK -- which changes the draft and therefore re-renders this
  // component -- leaves them alone.
  const tagOptions = useMemo(
    () => (tags ?? []).map((tag) => ({
      value: String(tag._id),
      label: tag.name,
      hint: tag.key,
    })),
    [tags],
  );
  const onChangeTagIds = useCallback(
    (tagIds: string[]) => setActivityDraft((d) => ({ ...d, tagIds })),
    [],
  );

  const dependencyOptions = useMemo(
    () => (catalogue ?? [])
      .filter((other) => String(other._id) !== activityDraft.activityId)
      .map((other) => ({
        value: String(other._id),
        label: other.title,
        hint: other.key,
      })),
    [catalogue, activityDraft.activityId],
  );
  const onChangeDependsOn = useCallback(
    (dependsOnActivityIds: string[]) =>
      setActivityDraft((d) => ({ ...d, dependsOnActivityIds })),
    [],
  );

  const tasksWithIssues = useMemo(
    () => activityDraft.tasks.map((task) => taskIssues(task).length),
    [activityDraft.tasks],
  );
  const totalIssueTasks = tasksWithIssues.filter((count) => count > 0).length;

  if (access === undefined) {
    return (
      <ActivityAdminLoadingState
        title="Activity editor"
        description="Checking your access to the activity editor."
        breadcrumbs={breadcrumbs}
      />
    );
  }

  if (!isActivityAdmin) {
    return (
      <ActivityAdminAccessDenied
        title="Activity editor"
        description="This page is for activity admins."
        breadcrumbs={breadcrumbs}
      />
    );
  }

  const tasks = activityDraft.tasks;
  const safeActiveIndex = tasks.length ? Math.min(activeTaskIndex, tasks.length - 1) : 0;
  const activeTask = tasks[safeActiveIndex];

  // Same reason as the field ids: a fresh array makes every sortable task tab
  // re-render regardless of its own memo.
  const taskIds = useMemo(() => tasks.map((_, i) => `task-${i}`), [tasks]);


  // Bound to the open tab once, rather than rebuilt on every render. Without
  // this the memo on TaskPanel buys nothing: four new function identities and
  // a fresh array are four reasons to re-render it, and typing the activity's
  // title supplied all of them on every keystroke.
  const otherTasks = useMemo(
    () => tasks.filter((_, index) => index !== safeActiveIndex),
    [tasks, safeActiveIndex],
  );
  const onChangeActiveTask = useCallback(
    (updater: Parameters<typeof updateTaskAt>[1]) => updateTaskAt(safeActiveIndex, updater),
    [updateTaskAt, safeActiveIndex],
  );
  const onRemoveActiveTask = useCallback(
    () => removeTaskAt(safeActiveIndex),
    [removeTaskAt, safeActiveIndex],
  );
  const onDuplicateActiveTask = useCallback(
    () => duplicateTaskAt(safeActiveIndex),
    [duplicateTaskAt, safeActiveIndex],
  );
  const onMoveActiveTask = useCallback(
    (direction: -1 | 1) => moveTaskAt(safeActiveIndex, direction),
    [moveTaskAt, safeActiveIndex],
  );


  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={isNew ? "New activity" : `Edit: ${savedTitle ?? "…"}`}
        loading={!isNew && activeActivityDetail === undefined}
        description="Design the steps, form fields, reviews and prerequisites of this activity."
        breadcrumbs={breadcrumbs}
      />

      <Stack gap={4} px={{ base: 4, sm: 6, lg: 8 }} py={{ base: 4, lg: 5 }}>
        {flashMessage ? (
          <Alert.Root status={flashMessage.tone === "error" ? "error" : "success"}>
            <Alert.Indicator />
            <Alert.Title>{flashMessage.text}</Alert.Title>
          </Alert.Root>
        ) : null}

        <Box
          as="form"
          onSubmit={(e: React.FormEvent) => {
            e.preventDefault();
            void handleSaveActivity();
          }}
        >
          <Stack gap={4}>
            {/* ── Basic information (includes Tags) ── */}
            <SectionCard
              title="Basic information"
              description="Name, dates, and whether it is required."
              icon={LuFileText}
            >
              <Field.Root>
                <Field.Label>Title</Field.Label>
                <Input
                  placeholder="e.g. Annual Review"
                  value={activityDraft.title}
                  onChange={(e) => {
                    const newTitle = e.target.value;
                    // Only derive the key from the title for a brand-new activity.
                    // Editing an existing activity keeps its key stable.
                    setActivityDraft((d) => ({
                      ...d,
                      title: newTitle,
                      key: isNew ? slugify(newTitle) : d.key,
                    }));
                  }}
                />
                {/* The key is derived from the title and shown nowhere: it is
                    how the server keeps titles from colliding and how the seed
                    refers to an activity, neither of which is the admin's
                    business. It was on screen only because it existed. */}
              </Field.Root>
              {/* Required sits on the title row rather than in a banner of its
                  own. It is one boolean, and a full-width card for it gave it
                  more of the form than the title it qualifies. */}
              <Flex align="center" gap={3} wrap="wrap">
                <Switch.Root
                  colorPalette="blue"
                  checked={activityDraft.required}
                  onCheckedChange={(e) => setActivityDraft((d) => ({ ...d, required: e.checked }))}
                >
                  <Switch.HiddenInput />
                  <Switch.Control />
                  <Switch.Label fontSize="sm">Required</Switch.Label>
                </Switch.Root>
                <Text fontSize="xs" color="fg.muted">
                  {activityDraft.required
                    ? "Counts towards an assignee's obligations, and can go overdue."
                    : "Encouraged rather than owed: it never goes overdue and blocks nothing."}
                </Text>
              </Flex>

              {/* Which programme page this appears on. A classification rather
                  than a date or a rule, so it sits with Required: both say
                  what the activity is, not when it happens. */}
              <Field.Root maxW="sm">
                <Field.Label>Programme</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    value={activityDraft.programme ?? ""}
                    onChange={(e) => setActivityDraft((d) => ({
                      ...d,
                      programme: e.target.value === "" ? null : e.target.value as ProgrammeValue,
                    }))}
                  >
                    <option value="">None</option>
                    {Object.entries(PROGRAMME_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </NativeSelect.Field>
                  <NativeSelect.Indicator />
                </NativeSelect.Root>
                <Field.HelperText>
                  {activityDraft.programme === null
                    ? "Not part of a programme: shown under My activities only."
                    : `Also shown on the ${PROGRAMME_LABELS[activityDraft.programme]} programme page of everyone who holds it, and in the progress matrix.`}
                </Field.HelperText>
              </Field.Root>

              <Field.Root>
                <Field.Label>Description</Field.Label>
                <Textarea
                  minH={16}
                  placeholder="Describe what this activity is about…"
                  value={activityDraft.description}
                  onChange={(e) =>
                    setActivityDraft((d) => ({
                      ...d,
                      description: e.target.value,
                    }))
                  }
                />
              </Field.Root>
              <SimpleGrid gap={3} columns={{ base: 1, lg: 3 }}>
                <DateRuleFields
                  label="Suggested start"
                  hint="Advice only. Anyone who wants to start earlier may."
                  anchor={activityDraft.startAnchor}
                  offsetDays={activityDraft.startOffsetDays}
                  fixedDay={activityDraft.startFixedDay}
                  onChange={(next) => setActivityDraft((d) => ({
                    ...d,
                    startAnchor: next.anchor,
                    startOffsetDays: next.offsetDays,
                    startFixedDay: next.fixedDay,
                  }))}
                />
                <DateRuleFields
                  label="Opens on"
                  hint="A real gate: before this day nothing can be submitted. Usually left empty."
                  anchor={activityDraft.opensAnchor}
                  offsetDays={activityDraft.opensOffsetDays}
                  fixedDay={activityDraft.opensFixedDay}
                  onChange={(next) => setActivityDraft((d) => ({
                    ...d,
                    opensAnchor: next.anchor,
                    opensOffsetDays: next.offsetDays,
                    opensFixedDay: next.fixedDay,
                  }))}
                />
                <DateRuleFields
                  label="Due"
                  hint="Leave the offset empty for no deadline at all."
                  anchor={activityDraft.dueAnchor}
                  offsetDays={activityDraft.dueOffsetDays}
                  fixedDay={activityDraft.dueFixedDay}
                  onChange={(next) => setActivityDraft((d) => ({
                    ...d,
                    dueAnchor: next.anchor,
                    dueOffsetDays: next.offsetDays,
                    dueFixedDay: next.fixedDay,
                  }))}
                />
              </SimpleGrid>

              {/* Tags and dependencies, side by side. Each is one search box,
                  and each had a full-width band of its own -- two thirds of the
                  form given to two controls. They pair because both say where
                  the activity sits rather than what it is. */}
              <SimpleGrid gap={6} columns={{ base: 1, lg: 2 }} borderTopWidth="1px" borderColor="border" pt={4}>
                <Stack gap={2}>
                  <HStack align="flex-start" gap={2}>
                    <Icon as={LuTag} boxSize={4} flexShrink={0} mt={0.5} color="blue.500" />
                    <Stack gap={0.5}>
                      <Heading size="sm">Tags</Heading>
                      <Text fontSize="xs" color="fg.muted">
                        Where this activity belongs, for admins and assignees alike.
                      </Text>
                    </Stack>
                  </HStack>
                  {tags === undefined ? (
                    <Text fontSize="sm" color="fg.muted">Loading tags…</Text>
                  ) : tags.length === 0 ? (
                    <Text fontSize="sm" color="fg.muted">
                      No tags yet. Create them in the{" "}
                      <TextLink>
                        <Link to="/activities/admin/catalogue">activity catalogue</Link>
                      </TextLink>
                      .
                    </Text>
                  ) : (
                    <MultiSelectTags
                      label="Tags"
                      hideLabel
                      placeholder="Search tags…"
                      emptyMessage="No tag matches that."
                      options={tagOptions}
                      selected={activityDraft.tagIds}
                      onChange={onChangeTagIds}
                    />
                  )}
                </Stack>

                <Stack gap={2}>
                  <HStack align="flex-start" gap={2}>
                    <Icon as={LuNetwork} boxSize={4} flexShrink={0} mt={0.5} color="blue.500" />
                    <Stack gap={0.5}>
                      <Heading size="sm">Comes after</Heading>
                      <Text fontSize="xs" color="fg.muted">
                        Until every one of these is complete, this activity cannot be.
                      </Text>
                    </Stack>
                  </HStack>
                  {catalogue === undefined ? (
                    <Text fontSize="sm" color="fg.muted">Loading activities…</Text>
                  ) : (
                    <MultiSelectTags
                      label="Comes after"
                      hideLabel
                      placeholder="Search activities…"
                      emptyMessage="No activity matches that."
                      options={dependencyOptions}
                      selected={activityDraft.dependsOnActivityIds}
                      onChange={onChangeDependsOn}
                    />
                  )}
                </Stack>
              </SimpleGrid>
            </SectionCard>

            {/* ── Steps (vertical tabs) ── */}
            <SectionCard
              title="Steps"
              description="Select a step to edit it; use the arrows to change the order."
              icon={LuClipboardList}
            >
              {tasks.length === 0 ? (
                <Box borderWidth="1px" borderStyle="dashed" borderColor="border" rounded="md" px={4} py={8} textAlign="center">
                  <Text mb={3} fontSize="sm" color="fg.muted">This activity has no steps yet.</Text>
                  <Button size="sm" colorPalette="blue" onClick={addTask}>
                    <LuPlus />
                    Add first step
                  </Button>
                </Box>
              ) : (
                <Tabs.Root
                  orientation="vertical"
                  value={String(safeActiveIndex)}
                  onValueChange={(e) => setActiveTaskIndex(Number(e.value))}
                  variant="plain"
                  display="flex"
                  flexDirection={{ base: "column", md: "row" }}
                  gap={4}
                  alignItems="stretch"
                >
                  {/* Left column: task list + add */}
                  <Stack gap={2} w={{ base: "full", md: "72" }} flexShrink={0}>
                    <Tabs.List display="flex" flexDirection="column" gap={2} border="none" p={0}>
                      <DndContext
                        sensors={taskSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleTaskDragEnd}
                        modifiers={VERTICAL_LIST_MODIFIERS}
                      >
                        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
                          {tasks.map((task, index) => (
                            <SortableTaskTab
                              key={index}
                              id={`task-${index}`}
                              index={index}
                              task={task}
                              issues={tasksWithIssues[index] ?? 0}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </Tabs.List>
                    <Button variant="outline" colorPalette="blue" w="full" onClick={addTask}>
                      <LuPlus />
                      Add step
                    </Button>
                  </Stack>

                  {/* Right column: selected task editor */}
                  <Box flex="1" minW={0} borderWidth="1px" borderColor="border" rounded="md" bg="bg" p={{ base: 3, sm: 4 }}>
                    <Tabs.Content value={String(safeActiveIndex)} p={0}>
                      {activeTask ? (
                        <TaskPanel
                          task={activeTask}
                          taskIndex={safeActiveIndex}
                          taskCount={tasks.length}
                          people={reviewerCandidates}
                          otherTasks={otherTasks}
                          onChangeTask={onChangeActiveTask}
                          onRemoveTask={onRemoveActiveTask}
                          onDuplicateTask={onDuplicateActiveTask}
                          onMoveTask={onMoveActiveTask}
                        />
                      ) : null}
                    </Tabs.Content>
                  </Box>
                </Tabs.Root>
              )}
            </SectionCard>
          </Stack>
        </Box>

        {/* ── Sticky action bar ── */}
        <Flex
          position="sticky"
          bottom={0}
          zIndex={10}
          mx={{ base: -4, sm: -6, lg: -8 }}
          px={{ base: 4, sm: 6, lg: 8 }}
          py={3}
          bg="bg"
          borderTopWidth="1px"
          borderColor="border"
          align="center"
          justify="space-between"
          gap={3}
          flexWrap="wrap"
        >
          <HStack gap={3}>
            {isDirty ? (
              <Text fontSize="xs" fontWeight="medium" color="orange.500">Unsaved changes</Text>
            ) : (
              <Text fontSize="xs" color="fg.muted">All changes saved</Text>
            )}
            {totalIssueTasks > 0 ? (
              <HStack gap={1} fontSize="xs" color="orange.500">
                <Icon as={LuCircleAlert} boxSize={3.5} />
                <Text>{totalIssueTasks} step{totalIssueTasks === 1 ? "" : "s"} need attention</Text>
              </HStack>
            ) : null}
          </HStack>
          <HStack gap={3}>
            <Button asChild variant="outline" colorPalette="red">
              <Link to="/activities/admin/catalogue">
                <LuX />
                Cancel
              </Link>
            </Button>
            <Button colorPalette="blue" onClick={() => void handleSaveActivity()}>
              <LuCheck />
              Save activity
            </Button>
          </HStack>
        </Flex>
      </Stack>
    </Box>
  );
}
